// 12-Step Recovery Programs organized by category
// Programs are sorted alphabetically by ID within each category

export type ProgramCategory = 'substance' | 'behavioral' | 'family';

export interface RecoveryProgram {
  id: string;
  name: string;
  shortName: string; // Acronym (AA, NA, etc.)
  description: string;
  category: ProgramCategory;
  websiteUrl: string;
  meetingFinderUrl?: string;
  color: string; // For UI theming
}

export const programCategories: Record<ProgramCategory, { label: string; description: string }> = {
  substance: {
    label: 'Substance Recovery',
    description: 'Programs for those recovering from alcohol, drug, and other substance addictions',
  },
  behavioral: {
    label: 'Behavioral Recovery',
    description: 'Programs for those recovering from behavioral and process addictions',
  },
  family: {
    label: 'Family & Relationship Support',
    description: 'Programs for families and friends affected by addiction',
  },
};

export const recoveryPrograms: RecoveryProgram[] = [
  // ========================================
  // Substance Recovery Programs (alphabetical by ID)
  // ========================================
  {
    id: 'aa',
    name: 'Alcoholics Anonymous',
    shortName: 'AA',
    description: 'For those who want to stop drinking',
    category: 'substance',
    websiteUrl: 'https://www.aa.org',
    meetingFinderUrl: 'https://www.aa.org/find-aa',
    color: '#1e40af', // Blue
  },
  {
    id: 'ca',
    name: 'Cocaine Anonymous',
    shortName: 'CA',
    description: 'For those recovering from cocaine and other substances',
    category: 'substance',
    websiteUrl: 'https://ca.org',
    meetingFinderUrl: 'https://ca.org/meetings',
    color: '#7c3aed', // Purple
  },
  {
    id: 'cma',
    name: 'Crystal Meth Anonymous',
    shortName: 'CMA',
    description: 'For those recovering from crystal meth addiction',
    category: 'substance',
    websiteUrl: 'https://www.crystalmeth.org',
    meetingFinderUrl: 'https://www.crystalmeth.org/meetings',
    color: '#0891b2', // Cyan
  },
  {
    id: 'dra',
    name: 'Dual Recovery Anonymous',
    shortName: 'DRA',
    description: 'For those with addiction and mental health challenges',
    category: 'substance',
    websiteUrl: 'https://draonline.org',
    color: '#4f46e5', // Indigo
  },
  {
    id: 'ha',
    name: 'Heroin Anonymous',
    shortName: 'HA',
    description: 'For those recovering from heroin addiction',
    category: 'substance',
    websiteUrl: 'https://heroinanonymous.org',
    meetingFinderUrl: 'https://heroinanonymous.org/meetings',
    color: '#b45309', // Amber
  },
  {
    id: 'ma',
    name: 'Marijuana Anonymous',
    shortName: 'MA',
    description: 'For those who want to stop using marijuana',
    category: 'substance',
    websiteUrl: 'https://marijuana-anonymous.org',
    meetingFinderUrl: 'https://marijuana-anonymous.org/find-a-meeting',
    color: '#15803d', // Emerald
  },
  {
    id: 'na',
    name: 'Narcotics Anonymous',
    shortName: 'NA',
    description: 'For those recovering from drug addiction',
    category: 'substance',
    websiteUrl: 'https://www.na.org',
    meetingFinderUrl: 'https://www.na.org/meetingsearch',
    color: '#166534', // Green
  },
  {
    id: 'pa',
    name: 'Pills Anonymous',
    shortName: 'PA',
    description: 'For those recovering from pill addiction',
    category: 'substance',
    websiteUrl: 'https://pillsanonymous.org',
    color: '#dc2626', // Red
  },

  // ========================================
  // Behavioral Recovery Programs (alphabetical by ID)
  // ========================================
  {
    id: 'da',
    name: 'Debtors Anonymous',
    shortName: 'DA',
    description: 'For those recovering from compulsive debting',
    category: 'behavioral',
    websiteUrl: 'https://debtorsanonymous.org',
    meetingFinderUrl: 'https://debtorsanonymous.org/meetings',
    color: '#059669', // Emerald
  },
  {
    id: 'faa',
    name: 'Food Addicts Anonymous',
    shortName: 'FAA',
    description: 'For those recovering from food addiction',
    category: 'behavioral',
    websiteUrl: 'https://www.foodaddictsanonymous.org',
    color: '#d97706', // Amber
  },
  {
    id: 'ga',
    name: 'Gamblers Anonymous',
    shortName: 'GA',
    description: 'For those recovering from gambling addiction',
    category: 'behavioral',
    websiteUrl: 'https://www.gamblersanonymous.org',
    meetingFinderUrl: 'https://www.gamblersanonymous.org/ga/locations',
    color: '#ca8a04', // Yellow
  },
  {
    id: 'itaa',
    name: 'Internet and Technology Addicts Anonymous',
    shortName: 'ITAA',
    description: 'For those with technology addiction',
    category: 'behavioral',
    websiteUrl: 'https://internetaddictsanonymous.org',
    color: '#2563eb', // Blue
  },
  {
    id: 'oa',
    name: 'Overeaters Anonymous',
    shortName: 'OA',
    description: 'For those with compulsive eating behaviors',
    category: 'behavioral',
    websiteUrl: 'https://oa.org',
    meetingFinderUrl: 'https://oa.org/find-a-meeting',
    color: '#ea580c', // Orange
  },
  {
    id: 'sa',
    name: 'Sexaholics Anonymous',
    shortName: 'SA',
    description: 'For those recovering from sexual addiction',
    category: 'behavioral',
    websiteUrl: 'https://www.sa.org',
    meetingFinderUrl: 'https://www.sa.org/meetings',
    color: '#be185d', // Pink
  },
  {
    id: 'saa',
    name: 'Sex Addicts Anonymous',
    shortName: 'SAA',
    description: 'For those recovering from sex addiction',
    category: 'behavioral',
    websiteUrl: 'https://saa-recovery.org',
    meetingFinderUrl: 'https://saa-recovery.org/meetings',
    color: '#9333ea', // Purple
  },
  {
    id: 'sca',
    name: 'Sexual Compulsives Anonymous',
    shortName: 'SCA',
    description: 'For those with sexually compulsive behavior',
    category: 'behavioral',
    websiteUrl: 'https://sca-recovery.org',
    meetingFinderUrl: 'https://sca-recovery.org/meetings',
    color: '#c026d3', // Fuchsia
  },
  {
    id: 'slaa',
    name: 'Sex and Love Addicts Anonymous',
    shortName: 'SLAA',
    description: 'For those with sex and love addiction',
    category: 'behavioral',
    websiteUrl: 'https://slaafws.org',
    meetingFinderUrl: 'https://slaafws.org/meetings',
    color: '#e11d48', // Rose
  },
  {
    id: 'ua',
    name: 'Underearners Anonymous',
    shortName: 'UA',
    description: 'For those with patterns of underearning',
    category: 'behavioral',
    websiteUrl: 'https://www.underearnersanonymous.org',
    color: '#0d9488', // Teal
  },
  {
    id: 'wa',
    name: 'Workaholics Anonymous',
    shortName: 'WA',
    description: 'For those with compulsive work behaviors',
    category: 'behavioral',
    websiteUrl: 'https://workaholics-anonymous.org',
    color: '#475569', // Slate
  },

  // ========================================
  // Family & Relationship Support (alphabetical by ID)
  // ========================================
  {
    id: 'aca',
    name: 'Adult Children of Alcoholics',
    shortName: 'ACA',
    description: 'For adults who grew up in alcoholic homes',
    category: 'family',
    websiteUrl: 'https://adultchildren.org',
    meetingFinderUrl: 'https://adultchildren.org/meeting-search',
    color: '#8b5cf6', // Violet
  },
  {
    id: 'al-anon',
    name: 'Al-Anon Family Groups',
    shortName: 'Al-Anon',
    description: 'For families and friends of alcoholics',
    category: 'family',
    websiteUrl: 'https://al-anon.org',
    meetingFinderUrl: 'https://al-anon.org/al-anon-meetings',
    color: '#0284c7', // Sky
  },
  {
    id: 'coda',
    name: 'Co-Dependents Anonymous',
    shortName: 'CoDA',
    description: 'For those recovering from codependency',
    category: 'family',
    websiteUrl: 'https://coda.org',
    meetingFinderUrl: 'https://coda.org/find-a-meeting',
    color: '#ec4899', // Pink
  },
  {
    id: 'cosa',
    name: 'COSA',
    shortName: 'COSA',
    description: "For those affected by others' sexual addiction",
    category: 'family',
    websiteUrl: 'https://cosa-recovery.org',
    meetingFinderUrl: 'https://cosa-recovery.org/meetings',
    color: '#f472b6', // Pink
  },
  {
    id: 'nar-anon',
    name: 'Nar-Anon Family Groups',
    shortName: 'Nar-Anon',
    description: 'For families and friends of addicts',
    category: 'family',
    websiteUrl: 'https://www.nar-anon.org',
    meetingFinderUrl: 'https://www.nar-anon.org/find-a-meeting',
    color: '#16a34a', // Green
  },
  {
    id: 's-anon',
    name: 'S-Anon International Family Groups',
    shortName: 'S-Anon',
    description: "For those affected by others' sexual addiction",
    category: 'family',
    websiteUrl: 'https://sanon.org',
    meetingFinderUrl: 'https://sanon.org/find-a-meeting',
    color: '#a855f7', // Purple
  },
];

// Helper functions
export function getProgramById(id: string): RecoveryProgram | undefined {
  return recoveryPrograms.find((p) => p.id === id);
}

export function getProgramsByCategory(category: ProgramCategory): RecoveryProgram[] {
  return recoveryPrograms
    .filter((p) => p.category === category)
    .sort((a, b) => a.id.localeCompare(b.id));
}

export function getProgramsByIds(ids: string[]): RecoveryProgram[] {
  return ids.map((id) => getProgramById(id)).filter((p): p is RecoveryProgram => p !== undefined);
}

export function getAllCategories(): ProgramCategory[] {
  return ['substance', 'behavioral', 'family'];
}
